/*
Attributes:
  value: the value that gets returned to something like a form
  help-tooltip-default-text: Default text for help tooltip
  help-tooltip-dict-key: Localization key for help tooltip
  help-placement: Position of help tooltip (default: 'right')
*/

import './as-help-hover.ts';
import { updateInputHelpHover } from './input-help-hover-utils.ts';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';

type SetCheckedOptions = {
  emitInput?: boolean;
  emitChange?: boolean;
  reflectAttr?: boolean;
  commit?: boolean;
};

class AsCheckbox extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['value', 'checked', 'label-default-text', 'label-dict-key', 'disabled', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement', 'paramate-paramName', 'paramate-paramname'];
  }

  private _inputEl: HTMLInputElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _helpHoverEl: HTMLElement | null = null;
  private _value = '';
  private _checked = false;
  private _lastCommittedChecked = false;
  private _isReflectingCheckedAttr = false;
  private _hasRendered = false;

  private _paramName: string = '';
  public get paramName(): string {
    return this._paramName;
  }
  public set paramName(val: string) {
    this._paramName = val;
    if (val) {
      this._inputEl?.setAttribute('data-paramate-paramname', val);
      this._inputEl?.classList.add('paramateParam');
    }
    else {
      if (this._inputEl?.hasAttribute('data-paramate-paramname')) {
        this._inputEl?.removeAttribute('data-paramate-paramname');
      }
      if (this._inputEl?.classList.contains('paramateParam')) {
        this._inputEl?.classList.remove('paramateParam');
      }
    }
  }

  constructor() {
    super();
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
  }

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }

    upgradeProperty(this, 'checked');
    upgradeProperty(this, 'value');

    if (this.hasAttribute('value')) {
      this._value = this.getAttribute('value') ?? '';
    }

    if (this.hasAttribute('checked')) {
      this._setCheckedInternal(true, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    } else {
      this._setCheckedInternal(false, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    }

    if (this.hasAttribute('paramate-paramName')) {
      this.paramName = this.getAttribute('paramate-paramName') ?? '';
    }
    if (this.hasAttribute('paramate-paramname')) {
      this.paramName = this.getAttribute('paramate-paramname') ?? '';
    }

    this._applyDisabledState();
    this._updateLabel();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('change', this._handleChange);
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('change', this._handleChange);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        this._value = newValue ?? '';
        if (this._inputEl) {
          this._inputEl.value = this._value;
        }
        break;
      case 'checked':
        if (this._isReflectingCheckedAttr) {
          return;
        }
        this._setCheckedInternal(newValue !== null, {
          emitInput: false,
          emitChange: true,
          reflectAttr: false,
          commit: true,
        });
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
      case 'disabled':
        this._applyDisabledState();
        break;
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
        this._updateHelpHover();
        break;
      case 'paramate-paramName':
      case 'paramate-paramname':
        this.paramName = newValue ?? '';
        break;
    }
  }

  get value(): string {
    return this._value;
  }

  set value(val: string) {
    this._value = val ?? '';
    if (this._inputEl) {
      this._inputEl.value = this._value;
    }
    this.setAttribute('value', this._value);
  }

  get checked(): boolean {
    return this._checked;
  }

  set checked(val: boolean) {
    this.setValue(Boolean(val));
  }

  public setValue(val: boolean, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange } = resolveSetValueSilence(opt);

    this._setCheckedInternal(Boolean(val), {
      emitInput: !silenceInput,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
    });
  }

  private _render(): void {
    // CRITICAL: Save hovercard BEFORE wiping innerHTML (which destroys all children)
    const savedHovercard = this.querySelector('as-hovercard');

    this.innerHTML = /* html */`
      <label class="h-flex" style="display:flex; flex-direction:row;width:100%;align-items:center;">
        <div style="display:flex;align-items:center;gap:4px;">
          <as-text class="label-text" dict-key="${this.getAttribute('label-dict-key') ?? ''}">${this.getAttribute('label-default-text') ?? 'Boolean'}</as-text>
          <span class="help-hover-container"></span>
        </div>
        <div class="spacer" style="flex:1;height:1px; background-color: gainsboro;align-self: center; margin:0 1rem;"></div>
        <input type="checkbox" />
      </label>
    `;
    this._inputEl = this.querySelector('input');
    this._labelTextEl = this.querySelector('.label-text');
    this._helpHoverEl = this.querySelector('.help-hover-container');

    if (this._inputEl) {
      this._inputEl.value = this._value;
      this._inputEl.checked = this._checked;
    }

    // Pass saved hovercard since it was destroyed by innerHTML
    this._updateHelpHover(savedHovercard);
  }

  private _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    this._setCheckedInternal(target.checked, {
      emitInput: true,
      emitChange: false,
      reflectAttr: true,
      commit: false,
    });
  }

  private _handleChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    this._setCheckedInternal(target.checked, {
      emitInput: false,
      emitChange: true,
      reflectAttr: true,
      commit: true,
    });
  }

  private _setCheckedInternal(checked: boolean, options: SetCheckedOptions): void {
    const {
      emitInput = false,
      emitChange = false,
      reflectAttr = false,
      commit = false,
    } = options;

    const previous = this._checked;
    const previousCommitted = this._lastCommittedChecked;
    this._checked = checked;

    if (this._inputEl) {
      this._inputEl.checked = checked;
    }

    if (reflectAttr) {
      this._isReflectingCheckedAttr = true;
      try {
        if (checked) {
          this.setAttribute('checked', '');
        } else {
          this.removeAttribute('checked');
        }
      } finally {
        this._isReflectingCheckedAttr = false;
      }
    }

    const shouldEmitInput = emitInput && previous !== checked;
    const shouldEmitChange =
      emitChange && (previous !== checked || previousCommitted !== checked);

    if (shouldEmitInput) {
      this._emitInput(checked, previous);
    }

    if (shouldEmitChange) {
      this._emitChange(checked, previousCommitted);
    }

    if (commit || shouldEmitChange) {
      this._lastCommittedChecked = checked;
    }
  }

  private _emitInput(checked: boolean, previous: boolean): void {
    const detail = {
      type: 'checkbox' as const,
      value: this._value,
      checked,
      previousChecked: previous,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-input', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(checked: boolean, previous: boolean): void {
    const detail = {
      type: 'checkbox' as const,
      value: this._value,
      checked,
      previousChecked: previous,
      phase: 'change' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    // this.dispatchEvent(new CustomEvent('change', { detail: { value: this._value, checked } }));
  }

  private _applyDisabledState(): void {
    if (!this._inputEl) return;
    const isDisabled = this.hasAttribute('disabled');
    this._inputEl.disabled = isDisabled;
  }

  private _updateLabel(): void {
    if (!this._labelTextEl) return;
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? 'Boolean';

    if (dictKey) {
      this._labelTextEl.setAttribute('dict-key', dictKey);
    } else {
      this._labelTextEl.removeAttribute('dict-key');
    }

    this._labelTextEl.textContent = defaultText;
  }

  private _updateHelpHover(savedHovercard?: Element | null): void {
    updateInputHelpHover(this, this._helpHoverEl, savedHovercard);
  }
}

customElements.define('as-checkbox', AsCheckbox);
